/**
 * Circling Restriction Parser
 *
 * Parses the freeform restriction text found on FAA approach plates in the
 * minima section (extracted from d-TPP PDF text layers). Converts recognized
 * patterns into structured sector / condition data for use in the circling-
 * minima text chip (Phase B) and map overlay (Phase C).
 *
 * Safety contract:
 *   - False negatives (missing a restriction) are worse than false positives.
 *   - Anything that doesn't match a known pattern falls through to
 *     restriction_kind: 'note' so the raw text surfaces to the pilot verbatim.
 *   - NEVER silently drop a restriction. If in doubt, 'note'.
 *
 * Source data: d-TPP PDF text layer extraction via api/admin/extract-dtpp-minima.ts
 */

// ─── Types ───────────────────────────────────────────────────────────────────

/** A single bearing sector for a circling restriction. */
export interface CirclingSector {
  /** Primary compass direction word, e.g. "NW", "S", "NE". */
  direction?: string;
  /** Sector start bearing (degrees true, 0-359). Measured clockwise. */
  from_deg?: number;
  /** Sector end bearing (degrees true, 0-359). Measured clockwise. */
  to_deg?: number;
  /**
   * Runway reference for the sector boundary.
   * E.g. "17" (single threshold), "17-35" (runway line), "10R-28L" (runway line).
   */
  rwy_ref?: string;
}

/**
 * Restriction kinds:
 *   na                — entire circling stadium is NA
 *   sector_restriction — specific direction(s) around the runway are restricted
 *   time_conditioned   — restriction applies at night or similar time window
 *   category_conditioned — restriction applies to specific aircraft categories
 *   note               — unrecognized pattern; raw_text surfaced verbatim
 */
export type RestrictionKind =
  | 'na'
  | 'sector_restriction'
  | 'time_conditioned'
  | 'category_conditioned'
  | 'note';

export interface ParsedRestriction {
  restriction_kind: RestrictionKind;
  sectors?: CirclingSector[];
  condition_text?: string;
}

// ─── Compass direction → bearing sector map ──────────────────────────────────
//
// Standard 8-point compass directions mapped to 45-degree bearing sectors.
// The sector represents the angular region centered on the named direction.
// "North of runway X" means the area bearing 315°-045° from the runway
// (i.e. the northern half of the compass rose).
//
// We use the convention: the named direction is the CENTER of the excluded sector,
// so "NW" excludes the 225°-315° region from the airport's perspective.
//
// Note: these are approximate. Phase C will use actual runway bearing for precise
// sector geometry. Phase B text chip shows the direction as text.

const COMPASS_SECTORS: Record<string, { from_deg: number; to_deg: number }> = {
  N:  { from_deg: 315, to_deg:  45 },
  NE: { from_deg:  22, to_deg:  112 },  // 22-112 (narrower near-NE slice)
  E:  { from_deg:  45, to_deg: 135 },
  SE: { from_deg: 112, to_deg:  202 },
  S:  { from_deg: 135, to_deg: 225 },
  SW: { from_deg: 202, to_deg:  292 },
  W:  { from_deg: 225, to_deg: 315 },
  NW: { from_deg: 292, to_deg:  22 },
};

// Broad cardinal + intercardinal list for regex
const DIR_PATTERN = 'N(?:E|W)?|S(?:E|W)?|E|W';

// ─── Pattern matchers ─────────────────────────────────────────────────────────

/**
 * Match: "CIRCLING NA" (entire stadium NA — no further qualification).
 * Captures only if the phrase appears without a following direction/condition.
 */
const RE_CIRCLING_NA_FULL = /^CIRCLING\s+NA\s*\.?\s*$/i;

/**
 * Match: "CIRCLING NA <DIR> OF RWY <N>" or "CIRCLING NA <DIR> OF RWY <N>-<M>"
 * Examples:
 *   "Circling NA NW of Rwy 17"
 *   "CIRCLING NA NE OF RWY 10R-28L"
 *   "Circling NA south of Rwy 10R-28L."
 */
const RE_CIRCLING_NA_SECTOR =
  /CIRCLING\s+NA\s+(NORTH(?:EAST|WEST)?|SOUTH(?:EAST|WEST)?|EAST|WEST|N(?:ORTH)?E(?:AST)?|N(?:ORTH)?W(?:EST)?|S(?:OUTH)?E(?:AST)?|S(?:OUTH)?W(?:EST)?|N(?:ORTH)?|S(?:OUTH)?|E(?:AST)?|W(?:EST)?)\s+OF\s+RWY\s+([\dA-Z]+(?:-[\dA-Z]+)?)/i;

/**
 * Match: "CIRCLING NA AT NIGHT" or similar time conditions.
 */
const RE_CIRCLING_NA_NIGHT = /CIRCLING\s+NA\s+AT\s+NIGHT/i;

/**
 * Match: "CIRCLING NA BETWEEN SUNSET AND SUNRISE" and similar.
 */
const RE_CIRCLING_NA_TIME = /CIRCLING\s+NA\s+(BETWEEN\s+SUNSET\s+AND\s+SUNRISE|AT\s+NIGHT|AFTER\s+DARK|DURING\s+NIGHT\s+OPERATIONS)/i;

/**
 * Match: "CATEGORY [A|B|C|D|E] NA" — specific category restrictions.
 * Examples:
 *   "CATEGORY D NA"
 *   "Cat D not authorized"
 */
const RE_CATEGORY_NA = /CAT(?:EGORY)?\s*([A-E](?:\s*(?:AND|&|,)\s*[A-E])*)\s+(?:NA|NOT\s+AUTHORIZED)/i;

// ─── Direction normalizer ─────────────────────────────────────────────────────

/** Normalize verbose direction words to 2-char compass codes. */
function normalizeDirection(dir: string): string {
  const upper = dir.toUpperCase().trim();
  const map: Record<string, string> = {
    N: 'N', NORTH: 'N', NTH: 'N',
    NE: 'NE', NORTHEAST: 'NE',
    E: 'E', EAST: 'E',
    SE: 'SE', SOUTHEAST: 'SE',
    S: 'S', SOUTH: 'S',
    SW: 'SW', SOUTHWEST: 'SW',
    W: 'W', WEST: 'W',
    NW: 'NW', NORTHWEST: 'NW',
  };
  if (map[upper]) return map[upper];
  // Try to match partial forms by checking first 2 chars
  const two = upper.substring(0, 2);
  if (map[two]) return map[two];
  return upper;
}

// ─── Main parser ──────────────────────────────────────────────────────────────

/**
 * Parse a single restriction text string extracted from a d-TPP plate.
 *
 * @param text  Raw text line, e.g. "Circling NA south of Rwy 10R-28L."
 * @returns     Structured restriction data.
 */
export function parseCirclingRestriction(text: string): ParsedRestriction {
  const trimmed = text.trim();

  // ── Full NA ─────────────────────────────────────────────────────────────────
  if (RE_CIRCLING_NA_FULL.test(trimmed)) {
    return { restriction_kind: 'na' };
  }

  // ── Time-conditioned NA ────────────────────────────────────────────────────
  const timeMatch = trimmed.match(RE_CIRCLING_NA_TIME);
  if (timeMatch || RE_CIRCLING_NA_NIGHT.test(trimmed)) {
    const condition = (timeMatch?.[1] ?? 'NIGHT').toUpperCase();
    return {
      restriction_kind: 'time_conditioned',
      condition_text: condition,
    };
  }

  // ── Category-conditioned NA ────────────────────────────────────────────────
  const catMatch = trimmed.match(RE_CATEGORY_NA);
  if (catMatch) {
    const cats = catMatch[1].toUpperCase().replace(/\s+AND\s+|&|,\s*/g, ',');
    return {
      restriction_kind: 'category_conditioned',
      condition_text: `CAT_${cats.replace(/,/g, '_')}`,
    };
  }

  // ── Sector restriction ─────────────────────────────────────────────────────
  const sectorMatch = trimmed.match(RE_CIRCLING_NA_SECTOR);
  if (sectorMatch) {
    const rawDir = sectorMatch[1];
    const rwyRef = sectorMatch[2].toUpperCase();
    const dir = normalizeDirection(rawDir);
    const bearings = COMPASS_SECTORS[dir];
    const sector: CirclingSector = {
      direction: dir,
      rwy_ref: rwyRef,
      ...(bearings ?? {}),
    };
    return {
      restriction_kind: 'sector_restriction',
      sectors: [sector],
    };
  }

  // ── Fallback: note ─────────────────────────────────────────────────────────
  // Anything unrecognized → 'note'. The raw_text will be stored verbatim so
  // the pilot can read it. Never silently drop.
  return { restriction_kind: 'note' };
}

/**
 * Parse all restriction lines from a plate's notes section.
 * Input is the list of text strings extracted from the plate PDF.
 *
 * @param lines  Array of text strings from the plate's notes region.
 * @returns      Parsed restrictions with their source raw text.
 */
export function parseCirclingRestrictions(
  lines: string[],
): Array<{ raw_text: string } & ParsedRestriction> {
  const results: Array<{ raw_text: string } & ParsedRestriction> = [];
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    // Guard: bare row-header "CIRCLING" (length <= 10) is not a restriction.
    // This prevents the minima-table header from leaking as a note.
    if (trimmed.length <= 10) continue;
    // Only process lines that mention circling or category restrictions.
    const lc = trimmed.toLowerCase();
    if (
      lc.includes('circling') ||
      lc.match(/cat(?:egory)?\s+[a-e]\s+(na|not authorized)/i)
    ) {
      const parsed = parseCirclingRestriction(trimmed);
      results.push({ raw_text: trimmed, ...parsed });
    }
  }
  return results;
}

// ─── Exported helpers for Phase B / Phase C ──────────────────────────────────

/**
 * Summarize restrictions for display in the Phase B text chip.
 * Returns a compact human-readable string, e.g. "NA NW of RWY 17; NA at night".
 */
export function formatRestrictionsForChip(
  restrictions: Array<{ raw_text: string } & ParsedRestriction>,
): string {
  if (restrictions.length === 0) return '';
  return restrictions
    .map((r) => {
      switch (r.restriction_kind) {
        case 'na':
          return 'NA (all directions)';
        case 'sector_restriction':
          if (r.sectors?.[0]) {
            const s = r.sectors[0];
            const rwy = s.rwy_ref ? ` of RWY ${s.rwy_ref}` : '';
            return `NA ${s.direction ?? ''}${rwy}`.trim();
          }
          return r.raw_text;
        case 'time_conditioned':
          return `NA ${r.condition_text ?? 'at night'}`;
        case 'category_conditioned':
          return `${r.condition_text?.replace('CAT_', 'Cat ').replace('_', '/')} NA`;
        case 'note':
        default:
          return r.raw_text;
      }
    })
    .join('; ');
}
